import { IPaginationReturn } from '../../interfaces/IPaginationReturn';
import { useRequest } from '../providers/useRequest';

import AppError from '../../errors/AppError';
import ITypeDocument from '../../interfaces/ITypeDocument';

const useTypeDocumentService = () => {
  const documentTypeRequest = useRequest<ITypeDocument>();
  const path = 'medical-certificate/type-documents';

  const list = async (
    perPage: number,
    currentPage: number,
    orderField: string,
    direction: 'asc' | 'desc',
  ): Promise<IPaginationReturn<ITypeDocument[]>> => {
    return await documentTypeRequest
      .getManyPaginated({
        path: `${path}?perPage=${perPage}&currentPage=${currentPage}&orderBy=${orderField}&orderDirection=${direction}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listById = async (id: number): Promise<ITypeDocument> => {
    return await documentTypeRequest
      .getOne({
        path: `${path}/${id}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const search = async (filter: string): Promise<ITypeDocument[]> => {
    if (filter === '') {
      filter = ' ';
    }

    const url = filter
      ? `${path}/search?perPage=300&currentPage=1&orderBy=description&orderDirection=asc&filter=${filter}`
      : `${path}/search?perPage=300&currentPage=1&orderBy=description&orderDirection=asc&filter= `;

    return await documentTypeRequest
      .getManyPaginated({
        path: url,
        sendAuthorization: true,
      })
      .then(result => {
        return result?.data;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const create = async (resource: ITypeDocument): Promise<ITypeDocument> => {
    return await documentTypeRequest
      .post({
        path: `${path}`,
        sendAuthorization: true,
        body: resource,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const update = async (id: number, resource: ITypeDocument): Promise<ITypeDocument> => {
    return await documentTypeRequest
      .put({
        path: `${path}/${id}`,
        sendAuthorization: true,
        body: resource,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const remove = async (id: number): Promise<void> => {
    return await documentTypeRequest
      .remove({
        path: `${path}/${id}`,
        sendAuthorization: true,
      })
      .then(() => {})
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  const listByUserId = async (userId: string): Promise<ITypeDocument[]> => {
    return await documentTypeRequest
      .getMany({
        path: `${path}/user-acess/${userId}`,
        sendAuthorization: true,
      })
      .then(result => {
        return result;
      })
      .catch(error => {
        throw new AppError(error.message);
      });
  };

  return { list, listById, search, create, update, remove, listByUserId };
};

export { useTypeDocumentService };
